import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Formik, Form } from 'formik';
import { useMediaQuery } from 'react-responsive';

import { LISTING_STATUS_ARCHIVED } from 'common/listingConstants';

import {
  MEDIA_QUERY_SMALL_SCREEN,
  MEDIA_QUERY_MEDIUM_SCREEN_850,
} from 'client/constants/mediaQueries';

import {
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrCard from 'client/components/SrCard/SrCard';
import SrModalV2, {
  MODAL_THEME_FIXED_FOOTER_BUTTON,
} from 'client/components/SrModalV2/SrModalV2';
import SrButton, {
  SR_BUTTON_THEME_BLUR,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_RED_INVERTED,
  SR_BUTTON_THEME_ADOBE_RED_OUTLINE,
} from 'client/components/SrButton/SrButton';
import SrFormikField, {
  SR_FIELD_THEME_PARTNER_FORM,
} from 'client/components/SrFormikField';

import ListingActions from 'client/actions/Listing';

function getFormFields() {
  return [
    {
      id: 'notifyApplicants',
      title: 'Notify non-approved applicants that the listing is no longer available?',
      type: 'yes_no',
      validations: ['required'],
      classnames: 'deny-title sunscreen partnerForm adobe',
    },
  ];
}

const DATE_FORMAT = 'MMM DD [at] h:mma';
const ArchiveModal = ({ onArchive, onClose }) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });
  const isMediumScreen = useMediaQuery({
    query: MEDIA_QUERY_MEDIUM_SCREEN_850,
  });

  return (
    <SrModalV2
      centerContent
      closeOnBackgroundClick
      showCloseButton={false}
      contentMaxWidth={!isSmallScreen ? '375px' : null}
      padding={isSmallScreen
        ? 'calc(100vh - 660px) 0 0 0'
        : '0 0 120px 0'
      }
      contentPadding='0'
      onClose={onClose}
      theme={isSmallScreen ? MODAL_THEME_FIXED_FOOTER_BUTTON : null}
      buttonTheme={isSmallScreen ? SR_BUTTON_THEME_ADOBE_GREY : SR_BUTTON_THEME_BLUR}
    >
      <SrBox
        borderRadius={isMediumScreen ? '14px' : '24px'}
        backgroundColor='white'
        boxShadow='0px 15px 26px rgba(212, 212, 212, 0.17)'
        boxSizing='border-box'
        maxWidth='375px'
        width={isSmallScreen ? 'calc(100vw - 20px)' : '375px'}
        padding='48px 40px 40px'
        margin={isMediumScreen ? '0 auto' : null}
      >
        <SrBox
          margin='0 auto'
          backgroundSize='cover'
          backgroundRepeat='no-repeat'
          backgroundPosition='center'
          backgroundImage='url(/images/caution-red.svg)'
          width='120px'
          height='104px'
          position='relative'
          zIndex='5'
          marginBottom='18px'
        />
        <SrFlex justify='center' direction='col'>
          <SrFlex marginBottom='16px' justify='center'>
            <SrText
              color='blueGreycliff'
              font='greycliff'
              weight='800'
              fontSize='26px'
              lineHeight='33px'
              align='center'
            >
              Archive Listing?
            </SrText>
          </SrFlex>
          <SrFlex marginBottom='9px' justify='center' direction='col'>
            <SrText
              color='greyMonochrome'
              font='greycliff'
              weight='600'
              fontSize='16px'
              lineHeight='24px'
              align='center'
            >
              Tenant screening will be disabled for this listing.
              The listing will move to the Archived Listings section of your portal.
            </SrText>
          </SrFlex>
          <SrBox>
            <Formik
              validate={(formValues) => {
                const formFields = getFormFields();
                const errors = createFormValidationFn(formFields)(formValues);

                return errors;
              }}
              enableReinitialize
              initialValues={createInitialValues({
                formFields: getFormFields(),
              })}
              onSubmit={onArchive}
              render={(formikBag) => {
                const formFields = getFormFields();

                return (
                  <Form autoComplete='off'>
                    <div>
                      {formFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                          className={formField.classnames || SR_FIELD_THEME_PARTNER_FORM}
                        />
                      ))}
                      <SrBox marginTop='18px' marginBottom='18px'>
                        <SrButton
                          type='submit'
                          theme={SR_BUTTON_THEME_ADOBE_RED_INVERTED}
                        >
                          Confirm
                        </SrButton>
                      </SrBox>
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_GREY}
                        onClick={onClose}
                      >
                        Cancel
                      </SrButton>
                    </div>
                  </Form>
                );
              }}
            />
          </SrBox>
        </SrFlex>
      </SrBox>
    </SrModalV2>
  );
};

const SrPartnerListingPageArchiveListing = ({
  listing,
  dispatch,
  reloadPartnerListing,
}) => {
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  let title = 'Archive Listing';
  let subtitle = 'Archive this listing once you’re finished screening tenants for this property.';
  let buttonTheme = SR_BUTTON_THEME_ADOBE_RED_OUTLINE;
  let buttonLabel = 'Archive Listing';
  let buttonClick = () => setShowArchiveModal(true);

  if (listing.status === LISTING_STATUS_ARCHIVED) {
    title = 'Archived Listing';
    subtitle = `Archived ${moment(listing.lastArchiveDate).format(DATE_FORMAT)}.`;
    buttonTheme = SR_BUTTON_THEME_ADOBE_GREY;
    buttonLabel = 'Archived';
    buttonClick = undefined;
  }

  const onArchive = async (formFields) => {
    await dispatch(
      ListingActions.archive,
      { listingId: listing.id, notifyApplicants: formFields.notifyApplicants },
    );
    await reloadPartnerListing();
    setShowArchiveModal(false);
  };

  return (
    <SrBox width='100%' marginTop='55px'>
      <SrCard maxWidth='100%'>
        <SrFlex justify='center' marginBottom='8px'>
          <SrText
            color='blueGreycliff'
            font='greycliff'
            weight='800'
            fontSize='26px'
            lineHeight='33px'
            align='center'
          >
            {title}
          </SrText>
        </SrFlex>
        <SrFlex justify='center' marginBottom='24px'>
          <SrText
            color='blueGreycliff'
            font='greycliff'
            weight='600'
            fontSize='16px'
            lineHeight='33px'
            align='center'
          >
            {subtitle}
          </SrText>
        </SrFlex>
        <SrButton
          theme={buttonTheme}
          onClick={buttonClick}
          disabled={listing.status === LISTING_STATUS_ARCHIVED}
        >
          {buttonLabel}
        </SrButton>
      </SrCard>
      {showArchiveModal && (
        <ArchiveModal
          onArchive={onArchive}
          onClose={() => setShowArchiveModal(false)}
        />
      )}
    </SrBox>
  );
};

SrPartnerListingPageArchiveListing.propTypes = {
  listing: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  reloadPartnerListing: PropTypes.func.isRequired,
};

SrPartnerListingPageArchiveListing.displayName = 'SrPartnerListingPageArchiveListing';

export default SrPartnerListingPageArchiveListing;
